import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Plus, TrendingUp, Lock, Store } from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface LocationAvailability {
  can_add_location: boolean;
  current_locations: number;
  locations_used: number;
  location_limit: number;
  locations_remaining: number;
  plan_name: string;
  plan_display_name: string;
  show_add_button: boolean;
  message: string;
}

const AddLocationCard = () => {
  const navigate = useNavigate();
  const [locationData, setLocationData] = useState<LocationAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  useEffect(() => {
    fetchLocationAvailability();
  }, []);

  const fetchLocationAvailability = async () => {
    try {
      const response = await apiClient.get('/restaurants/location-availability');
      setLocationData(response.data);
    } catch (error) {
      console.error('Failed to fetch location availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = () => {
    if (locationData?.can_add_location) {
      // Navigate to dedicated location setup page
      navigate('/location-setup');
    } else {
      setShowUpgradeDialog(true);
    }
  };

  // Don't show the card for Yummy plan users
  if (loading) return null;
  if (!locationData?.show_add_button) return null;

  const isLocked = !locationData.can_add_location;
  const cardOpacity = isLocked ? 'opacity-60' : 'opacity-100';

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mt-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">Multi-Location Management</h3>
          <span className="text-sm text-white/70">
            {locationData.locations_used} of {locationData.location_limit} locations used
          </span>
        </div>

        <motion.div
          whileHover={!isLocked ? { scale: 1.02 } : {}}
          whileTap={!isLocked ? { scale: 0.98 } : {}}
          onClick={handleAddLocation}
          className={`bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 cursor-pointer transition-all ${cardOpacity} ${
            !isLocked ? 'hover:bg-white/20' : ''
          } flex gap-4`}
        >
          {/* Restaurant Icon on Left */}
          <div className="flex-shrink-0">
            <div className={`p-3 ${isLocked ? 'bg-gray-500/20' : 'bg-gradient-to-br from-blue-500/20 to-purple-500/20'} rounded-xl`}>
              <Store className={`h-8 w-8 ${isLocked ? 'text-gray-400' : 'text-blue-400'}`} />
            </div>
          </div>

          {/* Content on Right */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-lg font-semibold text-white">
                {isLocked ? 'Location Limit Reached' : 'Add New Location'}
              </h4>
              <div className="flex items-center gap-2">
                {isLocked ? (
                  <Lock className="h-4 w-4 text-gray-400" />
                ) : (
                  <Plus className="h-4 w-4 text-green-400" />
                )}
                <MapPin className="h-4 w-4 text-white/60" />
              </div>
            </div>
            <p className="text-white/70 mb-3">
              {isLocked 
                ? `You've reached your ${locationData.plan_display_name} plan limit of ${locationData.location_limit} locations`
                : `Expand your restaurant to ${locationData.locations_remaining} more location${locationData.locations_remaining > 1 ? 's' : ''}`
              }
            </p>
          
            {locationData.locations_used > 0 && (
              <div className="mt-3 p-3 bg-white/5 rounded-lg">
                <p className="text-sm text-green-300 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Multi-location restaurants see 35% higher revenue
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-md bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] border-white/20 shadow-2xl shadow-black/50 backdrop-blur-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Lock className="h-5 w-5 text-yellow-400" />
              Upgrade to Add More Locations
            </DialogTitle>
            <DialogDescription className="text-white/80">
              You've reached the maximum of {locationData?.location_limit} locations on your {locationData?.plan_display_name} plan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <p className="text-white/90 mb-3">
                Upgrade to <span className="text-yellow-400 font-semibold">Gourmet</span> to manage up to 4 locations and unlock:
              </p>
              <ul className="space-y-2 text-white/80 text-sm">
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 bg-yellow-400 rounded-full" />
                  Marketing automation for all locations
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 bg-yellow-400 rounded-full" />
                  Advanced AI insights across your chain
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 bg-yellow-400 rounded-full" />
                  500 AI-generated images per year
                </li>
              </ul>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/products/virtual-assistant')}
                className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 hover:from-yellow-500 hover:to-yellow-600 font-medium transition-all shadow-lg"
              >
                View Plans
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowUpgradeDialog(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/30 transition-all"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddLocationCard;